import { Wallet, Search, ListFilter, RotateCcw, Database, Wifi } from "lucide-react";
import BundleActivationModal from "./components/BundleActivationModal";
import BundleReceipt from "./components/BundleReceipt";
import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { getAgentBundleOrders } from "../../../services/bundleService";
import { handleSetSearch } from "../../../utils/searchDebouncing.js";
import ARHCustomDataTable from "../../../components/dataTable/ARHCustomDataTable.jsx";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

function BundleActivation() {
  const { t } = useTranslation();
  const [activationModalOpen, setActivationModalOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const searchRef = useRef(null);
  const userInfo = useSelector((state) => state.auth.user);
  const [refetch, setRefetch] = useState(false);

  const [filter, setFilter] = useState({
    search: "",
    limit: 10,
    page: 1,
    status: "",
    startDate: "",
    endDate: "",
    receiver: "",
    operator: "",
    minAmount: "",
    maxAmount: "",
  });

  const closeActivationModal = () => setActivationModalOpen(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["bundleOrders", filter, refetch, userInfo?.uid],
    queryFn: () => getAgentBundleOrders(userInfo?.uid, filter),
    enabled: !!userInfo?.uid,
  });

  const handleRefetch = () => setRefetch((prev) => !prev);
  
  const handleActivationSuccess = () => {
    setActivationModalOpen(false);
    setRefetch((prev) => !prev);
    toast.success(t('bundleActivation.activationInitiated'));
  };

  const handleSearchChange = (value) => {
    setFilter((prev) => ({
      ...prev,
      search: value,
      page: 1
    }));
  };

  const handlePageChange = (page) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const handleLimitChange = (limit) => {
    setFilter((prev) => ({ ...prev, limit, page: 1 }));
  };

  const handleStatusFilter = (status) => {
    setFilter((prev) => ({ 
      ...prev, 
      status: status === "all" ? "" : status,
      page: 1 
    }));
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "succeeded":
        return "text-green-700 bg-green-100";
      case "failed":
      case "cancelled":
        return "text-red-700 bg-red-100";
      case "processing":
      case "pending":
        return "text-orange-700 bg-orange-100";
      default:
        return "text-gray-700 bg-gray-100";
    }
  };

  const getStatusText = (status) => {
    if (!status) return "-";
    const statusKey = status.toLowerCase();
    return t(`bundleActivation.status.${statusKey}`) || status;
  };

  const columns = [
    { 
      key: "txnNumber", 
      label: t('bundleActivation.columns.transactionId'),
      render: (item) => (
        <div className="font-mono text-sm">
          {item?.txnNumber || "-"}
        </div>
      )
    },
    { 
      key: "receiver", 
      label: t('bundleActivation.columns.receiver'),
      render: (item) => (
        <div className="flex items-center gap-2">
          <Wifi size={14} className="text-primary-500" />
          <span>{item?.receiver || "-"}</span>
        </div>
      )
    },
    { 
      key: "amount", 
      label: t('bundleActivation.columns.amount'),
      render: (item) => (
        <div className="font-semibold">
          {parseFloat(item?.amount || 0).toFixed(2)} {t('common.currency')}
        </div>
      )
    },
    { 
      key: "totalAmount", 
      label: t('bundleActivation.columns.total'),
      render: (item) => (
        <div className="font-semibold text-green-600">
          {parseFloat(item?.totalAmount || item?.amount || 0).toFixed(2)} {t('common.currency')}
        </div>
      )
    },
    { 
      key: "operator", 
      label: t('bundleActivation.columns.operator'),
      render: (item) => (
        <div className="flex items-center gap-2">
          <Database size={14} className="text-purple-500" />
          <span>{item?.operator || "-"}</span>
        </div>
      )
    },
    {
      key: "product",
      label: t('bundleActivation.columns.product'),
      render: (item) => (
        <div className="max-w-[200px] truncate" title={item?.productDetails?.productName?.en || item?.productDetails?.productName}>
          {item?.productDetails?.productName?.en || item?.productDetails?.productName || "-"}
        </div>
      )
    },
    {
      key: "status",
      label: t('bundleActivation.columns.status'),
      render: (item) => (
        <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center justify-center ${getStatusColor(item?.status)}`}>
          {getStatusText(item?.status)}
        </div>
      )
    },
    {
      key: "createdAt",
      label: t('bundleActivation.columns.createdAt'),
      render: (item) => (
        <div className="text-sm">
          {item?.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-"}
          <div className="text-xs text-gray-500">
            {item?.createdAt ? new Date(item.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ""}
          </div>
        </div>
      )
    },
    {
      key: "actions",
      label: t('bundleActivation.columns.actions'),
      render: (item) => (
        <div className="flex gap-2">
          <button 
            onClick={() => {
              setSelectedTransaction(item);
              setReceiptOpen(true);
            }}
            className="text-primary-600 hover:text-primary-800 text-sm px-2 py-1 border border-primary-600 rounded hover:bg-primary-50 transition-colors"
          >
            {t('bundleActivation.actions.receipt')}
          </button>
        </div>
      )
    }
  ];

  const customEmptyState = (
    <div className="w-full min-h-[200px] flex flex-col items-center justify-center">
      <img 
        src="/images/Search.png" 
        alt={t('common.noDataFound')}
        className="w-[170px] h-[170px]"
      />
      <p className="mt-2 text-gray-500">{t('bundleActivation.noActivationsFound')}</p>
      <button
        onClick={() => setActivationModalOpen(true)}
        className="mt-4 px-4 py-2 bg-[#CD0C02] text-white rounded hover:bg-[#b30b02]"
      >
        {t('bundleActivation.activateFirstBundle')}
      </button>
    </div>
  );

  const customErrorState = (
    <div className="w-full min-h-[200px] flex flex-col items-center justify-center">
      <img 
        src="/images/Search.png" 
        alt={t('common.error')}
        className="w-[170px] h-[170px]"
      />
      <div className="text-sm text-red-600 mb-2">
        {error?.message || t('bundleActivation.loadFailed')}
      </div>
      <button
        onClick={handleRefetch}
        className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
      >
        {t('common.retry')}
      </button>
    </div>
  );

  return (
    <div className="w-full px-5 py-4 min-h-screen bg-gray-50 dark:bg-gray-900">
      <BundleActivationModal 
        open={activationModalOpen} 
        onClose={closeActivationModal} 
        onSuccess={handleActivationSuccess} 
      />
      
      <BundleReceipt
        transaction={selectedTransaction} 
        isOpen={receiptOpen} 
        onClose={() => setReceiptOpen(false)} 
      />
      
      <div className="w-full">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
              {t('bundleActivation.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              {t('bundleActivation.subtitle')}
            </p>
          </div>
        </div>
      </div>

      <div className="w-full flex flex-col md:flex-row items-center justify-between gap-5 mt-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center w-full md:w-[70%] gap-3 sm:gap-5">
          <div className="relative w-full sm:max-w-[371px]">
            <input
              ref={searchRef}
              onChange={(e) => handleSetSearch(handleSearchChange, e.target.value, searchRef)}
              type="text"
              placeholder={t('bundleActivation.searchPlaceholder')}
              defaultValue={filter.search}
              className="w-full h-[44px] pl-10 pr-4 border border-[#E4E7EC] dark:border-gray-600 rounded-md bg-transparent dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <Search size={18} className="absolute top-[13px] left-3 text-gray-400" />
          </div>

          <div className="relative group">
            <button className="flex items-center gap-2 px-4 py-2 h-[44px] border border-[#E4E7EC] dark:border-gray-600 rounded-md bg-gradient-to-r from-[#F8F4FF] to-[#FAF9F3] dark:from-slate-800 dark:to-slate-800 hover:bg-gray-50 dark:hover:bg-gray-700">
              <ListFilter size={20} className="text-[#667085] dark:text-gray-300" />
              <span className="text-[#667085] dark:text-gray-300 text-[14px]">
                {t('bundleActivation.filter')}
              </span>
            </button>
            
            <div className="absolute left-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50 hidden group-hover:block">
              <div className="py-1">
                <div className="px-3 py-2 border-b dark:border-gray-700">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t('bundleActivation.filterByStatus')}
                  </span>
                </div>
                {["all", "pending", "processing", "succeeded", "failed", "cancelled"].map((status) => (
                  <button
                    key={status}
                    onClick={() => handleStatusFilter(status)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${
                      (filter.status === status) || (!filter.status && status === "all") 
                        ? "text-[#CD0C02] font-medium dark:text-red-400" 
                        : "text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {t(`bundleActivation.status.${status}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center justify-end w-full md:w-[30%] gap-3">
          <button
            onClick={handleRefetch}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-sm flex items-center gap-2 dark:text-white"
          >
            <RotateCcw size={16} />
            {t('bundleActivation.refresh')}
          </button>
          <button
            onClick={() => setActivationModalOpen(true)}
            className="bg-[#CD0C02] px-4 py-2 rounded-md text-white text-sm hover:bg-[#b30b02] flex items-center gap-2"
          >
            <Wifi size={16} />
            {t('bundleActivation.activateBundle')}
          </button>
        </div>
      </div>

      <div className="mt-5">
        <ARHCustomDataTable
          data={data?.data?.orders || []}
          columns={columns}
          meta={data?.data?.pagination || {
            page: filter.page,
            pages: Math.ceil((data?.data?.pagination?.total || 0) / filter.limit) || 1,
            total: data?.data?.pagination?.total || 0,
            limit: filter.limit
          }}
          loading={isLoading}
          isError={isError}
          error={error}
          onRetry={handleRefetch}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          searchQuery={filter.search}
          emptyState={customEmptyState}
          errorState={customErrorState}
          rowClassName="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
        />
      </div>
    </div>
  );
}

export default BundleActivation;